import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { modifyLoginErrorMessage } from '../utils/apiErrors'
import { ValidateIdentityMethod } from '../interfaces/services/validate-identity.interface'
import { ErrorMessageObject } from '../interfaces/services/errors.interface'

type dynamicForm = 'LoginForm' | 'DoubleFaForm' | 'PasswordUpdateForm' | 'ValidateIdentityFormView'
type validateIdentityDynamicForm = 'RequestForm' | 'WhatsAppForm' | 'OTPForm' | 'TemporarilyBlockedForm'

export const useLoginStore = defineStore('login', () => {
  const loadingRef = ref<boolean>(false)
  const loadingAuxRef = ref<boolean>(false)
  const googleLoadingRef = ref<boolean>(false)
  const error = ref<string | null>(null)
  const errorObject = ref<ErrorMessageObject | null>(null)
  const error2FA = ref<boolean>(false)

  const emailRef = ref<string>('')
  const passwordRef = ref<string>('')
  const googleIdTokenRef = ref<string | null>(null)
  const required2FARef = ref<boolean>(false)
  const loginDynamicFormRef = ref<dynamicForm>('LoginForm')
  const validateIdentityDynamicFormRef = ref<validateIdentityDynamicForm>('RequestForm')
  const validateIdentityTypeRef = ref<ValidateIdentityMethod>('EMAIL')
  const userFailuresRef = ref<number>(0)
  const hasWhatsappConfiguredRef = ref<boolean>(false)
  const hasPublicityBannerRef = ref<boolean>(false)

  const isLoading = computed(() => loading.value || googleLoading.value)

  const loading = computed({
    get: () => loadingRef.value,
    set: (value: boolean) => (loadingRef.value = value),
  })

  const loadingAux = computed({
    get: () => loadingAuxRef.value,
    set: (value: boolean) => (loadingAuxRef.value = value),
  })

  const googleLoading = computed({
    get: () => googleLoadingRef.value,
    set: (value: boolean) => (googleLoadingRef.value = value),
  })

  const email = computed({
    get: () => emailRef.value,
    set: (value: string) => (emailRef.value = value),
  })

  const password = computed({
    get: () => passwordRef.value,
    set: (value: string) => (passwordRef.value = value),
  })

  const googleIdToken = computed({
    get: () => googleIdTokenRef.value,
    set: (value: string) => (googleIdTokenRef.value = value),
  })

  const required2FA = computed({
    get: () => required2FARef.value,
    set: (value: boolean) => (required2FARef.value = value),
  })

  const loginDynamicForm = computed({
    get: () => loginDynamicFormRef.value,
    set: (value: dynamicForm) => (loginDynamicFormRef.value = value),
  })

  const validateIdentityDynamicForm = computed({
    get: () => validateIdentityDynamicFormRef.value,
    set: (value: validateIdentityDynamicForm) => (validateIdentityDynamicFormRef.value = value),
  })

  const validateIdentityType = computed({
    get: () => validateIdentityTypeRef.value,
    set: (value: ValidateIdentityMethod) => (validateIdentityTypeRef.value = value),
  })

  const hasWhatsappConfigured = computed({
    get: () => hasWhatsappConfiguredRef.value,
    set: (value: boolean) => (hasWhatsappConfiguredRef.value = value),
  })

  const hasPublicityBanner = computed({
    get: () => hasPublicityBannerRef.value,
    set: (value: boolean) => (hasPublicityBannerRef.value = value),
  })

  const userFailures = computed({
    get: () => userFailuresRef.value,
    set: (value: number) => (userFailuresRef.value = value),
  })

  const setHasWhatsappConfigured = (value: boolean) => {
    hasWhatsappConfigured.value = value
  }

  const setError = (message?: string, code?: number | string) => {
    const defaultErrorMessage =
      'Estamos experimentando fallas técnicas ahora mismo. Intenta de nuevo en un momento'

    if (!message) {
      error.value = defaultErrorMessage
      return
    }

    if (code) {
      error.value = modifyLoginErrorMessage(message, code) ?? defaultErrorMessage
      return
    }

    error.value = message
  }

  const setErrorObject = ({ title, body }: ErrorMessageObject) => {
    errorObject.value = { title, body }
  }

  const setError2FA = (value: boolean) => {
    error2FA.value = value
  }

  const clearErrors = () => {
    error.value = null
    errorObject.value = null
  }

  const $reset = () => {
    email.value = ''
    password.value = ''
    googleIdTokenRef.value = null
    required2FA.value = false
    loginDynamicForm.value = 'LoginForm'
    validateIdentityDynamicForm.value = 'RequestForm'
    validateIdentityType.value = 'EMAIL'
    error2FA.value = false
    hasWhatsappConfigured.value = false
    clearErrors()
  }

  return {
    loading,
    loadingAux,
    googleLoading,
    isLoading,
    email,
    password,
    googleIdToken,
    required2FA,
    loginDynamicForm,
    validateIdentityDynamicForm,
    validateIdentityType,
    userFailures,
    hasWhatsappConfigured,
    hasPublicityBanner,
    error,
    errorObject,
    error2FA,
    setError,
    setErrorObject,
    setError2FA,
    clearErrors,
    $reset,
    setHasWhatsappConfigured,
  }
})
